import { useEffect, useRef, useState } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import BullIcon from "@mui/icons-material/TrendingUpRounded";
import BearIcon from "@mui/icons-material/TrendingDownRounded";
import NeutralIcon from "@mui/icons-material/RemoveRounded";

import { num, pct, tone } from "../api";
import type { Decision, Quote } from "../api";

/**
 * Flash a price green or red for a moment when it moves.
 *
 * The dashboard polls every 4 seconds, so a price that has changed looks
 * identical to one that has not — which is what made a live ticker read as
 * frozen. The flash is the only signal that a poll actually brought new data.
 */
function useTick(ltp: number): "up" | "down" | "" {
  const prev = useRef(ltp);
  const [dir, setDir] = useState<"up" | "down" | "">("");

  useEffect(() => {
    if (ltp === prev.current) return;
    setDir(ltp > prev.current ? "up" : "down");
    prev.current = ltp;
    const id = window.setTimeout(() => setDir(""), 700);
    return () => window.clearTimeout(id);
  }, [ltp]);

  return dir;
}

function TickerCell({ q }: { q: Quote }) {
  const t = tone(q.change);
  const dir = useTick(q.ltp);

  return (
    <Box
      sx={{
        // Fixed basis so cells keep their shape; the row scrolls on a phone
        // rather than squeezing four quotes into 375px.
        flex: "1 0 158px",
        minWidth: 158,
        px: 2,
        py: 1.4,
        borderRight: "1px solid var(--border)",
        "&:last-of-type": { borderRight: "none" },
        transition: "background 220ms ease",
        background: dir === "up"
          ? "rgba(0,230,118,.10)"
          : dir === "down"
            ? "rgba(244,67,54,.10)"
            : "transparent",
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.8}>
        <Typography
          sx={{
            fontSize: ".66rem",
            fontWeight: 800,
            letterSpacing: ".07em",
            color: "var(--muted)",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {q.label}
        </Typography>
        <Box
          sx={{
            fontSize: ".56rem",
            fontWeight: 800,
            px: 0.6,
            borderRadius: "4px",
            color: "var(--muted)",
            background: "#141d29",
            border: "1px solid var(--border)",
          }}
        >
          {q.exchange}
        </Box>
        {dir && (
          <Box
            component="span"
            className={dir === "up" ? "up" : "down"}
            sx={{ fontSize: ".6rem", fontWeight: 900, ml: "auto" }}
          >
            {dir === "up" ? "▲" : "▼"}
          </Box>
        )}
      </Stack>

      <Stack direction="row" alignItems="baseline" gap={1} sx={{ mt: 0.5 }}>
        <Typography
          className="mono"
          sx={{
            fontSize: "1.02rem",
            fontWeight: 800,
            transition: "color 220ms ease",
            color: dir === "up"
              ? "var(--green)"
              : dir === "down"
                ? "var(--red)"
                : "var(--text-2)",
          }}
        >
          ₹{num(q.ltp)}
        </Typography>
        <Typography
          className={`mono ${t}`}
          sx={{ fontSize: ".72rem", fontWeight: 700, whiteSpace: "nowrap" }}
        >
          {q.change > 0 ? "+" : ""}
          {num(q.change)} ({pct(q.change_pct)})
        </Typography>
      </Stack>
    </Box>
  );
}

interface TickerStripProps {
  quotes: Quote[];
  decisions: Record<string, Decision>;
}

/**
 * Aggregate the per-symbol decisions into one desk-level read.
 *
 * Bias comes from what the engine actually fired (a FLAT symbol contributes
 * nothing), and the headline regime is whichever one the most symbols are in.
 */
function sentiment(decisions: Record<string, Decision>) {
  const list = Object.values(decisions);
  let long = 0;
  let short = 0;
  const regimes = new Map<string, number>();

  for (const d of list) {
    regimes.set(d.regime, (regimes.get(d.regime) ?? 0) + 1);
    if (d.fired?.direction === "LONG") long += 1;
    if (d.fired?.direction === "SHORT") short += 1;
  }

  const regime =
    [...regimes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";
  const bias = long > short ? "Bullish" : short > long ? "Bearish" : "Neutral";
  return { bias, regime, long, short, total: list.length };
}

/**
 * Overlay streamed ticks on top of the polled dashboard payload.
 *
 * The poll runs every 4 seconds and is behind the bot's throttled REST calls;
 * the stream pushes each tick as it arrives. Polled quotes remain the base so
 * the strip still renders — with prev_close, lot size and exchange — if the
 * stream is down or the browser drops the connection.
 */
function useStream(base: Quote[]): { quotes: Quote[]; live: boolean } {
  const [ticks, setTicks] = useState<Record<string, Partial<Quote>>>({});
  const [live, setLive] = useState(false);

  useEffect(() => {
    const es = new EventSource("/api/stream");
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        setLive(Boolean(d.stats?.connected));
        setTicks((prev) => {
          const next = { ...prev };
          for (const q of d.quotes ?? []) next[q.symbol] = q;
          return next;
        });
      } catch {
        /* a malformed frame must not tear down the stream */
      }
    };
    es.onerror = () => setLive(false);
    return () => es.close();
  }, []);

  return {
    quotes: base.map((q) => (ticks[q.symbol] ? { ...q, ...ticks[q.symbol] } : q)),
    live,
  };
}

export default function TickerStrip({ quotes: polled, decisions }: TickerStripProps) {
  const { quotes, live } = useStream(polled);
  const s = sentiment(decisions);
  const bull = s.bias === "Bullish";
  const bear = s.bias === "Bearish";

  return (
    <Stack direction="row" gap={1.5} alignItems="stretch">
      <Box
        className="rail panel3d"
        sx={{ flex: 1, minWidth: 0, px: 0.5, py: 0.5 }}
      >
        {quotes.map((q) => (
          <TickerCell key={q.symbol} q={q} />
        ))}
      </Box>

      {/* desk sentiment */}
      <Box
        className="panel3d"
        sx={{
          flex: "0 0 210px",
          p: 2,
          position: "relative",
          overflow: "hidden",
          background: bull
            ? "linear-gradient(150deg,#0e3722 0%,#0a2417 60%,#071b11 100%)"
            : bear
              ? "linear-gradient(150deg,#3a1414 0%,#280e0e 60%,#1c0909 100%)"
              : "linear-gradient(150deg,#1a2330 0%,#121a24 100%)",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.7}>
          <Typography
            sx={{ fontSize: ".68rem", fontWeight: 800, color: "rgba(255,255,255,.62)" }}
          >
            Market Sentiment
          </Typography>
          <Tooltip title={live
            ? "Streaming ticks over Dhan's WebSocket feed"
            : "Stream offline — prices are from the 4-second poll"}>
            <Box sx={{
              ml: "auto", px: 0.7, borderRadius: "4px", fontSize: ".52rem",
              fontWeight: 900, letterSpacing: ".05em", cursor: "help",
              color: live ? "var(--green)" : "var(--muted)",
              border: `1px solid ${live ? "rgba(0,230,118,.4)" : "var(--border)"}`,
            }}>
              {live ? "● STREAM" : "POLL"}
            </Box>
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.8} sx={{ mt: 0.4 }}>
          {bull ? (
            <BullIcon sx={{ fontSize: 22, color: "var(--green)" }} />
          ) : bear ? (
            <BearIcon sx={{ fontSize: 22, color: "var(--red)" }} />
          ) : (
            <NeutralIcon sx={{ fontSize: 22, color: "var(--muted)" }} />
          )}
          <Typography
            sx={{
              fontSize: "1.24rem",
              fontWeight: 900,
              color: bull ? "var(--green)" : bear ? "var(--red)" : "var(--text)",
            }}
          >
            {s.bias}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: ".67rem", color: "var(--muted)", mt: 0.6 }}>
          Regime <b style={{ color: "var(--purple)" }}>{s.regime}</b> · {s.long}L /{" "}
          {s.short}S fired of {s.total}
        </Typography>
      </Box>
    </Stack>
  );
}
